import { useEffect, useState } from "react";

/**
 * The product height a case job normalises to (owner, 2026-09-25): a Fixed height, as % of
 * the 1024² canvas (70–98, default 95), or No height restriction — for a product in another
 * orientation, which is then only fitted inside the canvas. `null` is "no restriction".
 */
export const PRODUCT_HEIGHT_DEFAULT = 95;
export const PRODUCT_HEIGHT_MIN = 70;
export const PRODUCT_HEIGHT_MAX = 98;

function clampHeight(text: string): number {
  const n = Math.round(Number(text));
  if (!text.trim() || !Number.isFinite(n)) return PRODUCT_HEIGHT_DEFAULT;
  return Math.min(PRODUCT_HEIGHT_MAX, Math.max(PRODUCT_HEIGHT_MIN, n));
}

export function ProductHeightControl({
  value, onChange, testId = "product-height",
}: {
  value: number | null;
  onChange: (value: number | null) => void;
  testId?: string;
}) {
  const fixed = value !== null;
  // The field holds what is typed; clamping on every key would turn the "8" of "85" into 70.
  const [draft, setDraft] = useState(String(value ?? PRODUCT_HEIGHT_DEFAULT));
  useEffect(() => { if (value !== null) setDraft(String(value)); }, [value]);
  const commit = () => {
    const next = clampHeight(draft);
    setDraft(String(next));
    onChange(next);
  };
  return (
    <div className="field" data-testid={testId}>
      <span className="label">Product height</span>
      <select className="input" value={fixed ? "fixed" : "none"} data-testid={`${testId}-mode`}
        aria-label="Product height mode"
        onChange={(e) => onChange(e.target.value === "fixed" ? PRODUCT_HEIGHT_DEFAULT : null)}>
        <option value="fixed">Fixed height (%)</option>
        <option value="none">No height restriction</option>
      </select>
      {fixed && (
        <input className="input" type="number" min={PRODUCT_HEIGHT_MIN} max={PRODUCT_HEIGHT_MAX} step={1}
          value={draft} aria-label="Product height, % of the canvas" data-testid={`${testId}-pct`}
          onChange={(e) => setDraft(e.target.value)} onBlur={commit} />
      )}
      <small className="caption">How tall the product sits in the final image.</small>
    </div>
  );
}
